import Vue from "vue";
import Vuex from "vuex";
import VuexPersist from "vuex-persist";
import axios from "axios";
import cfg from "@/cfg";
import notifications from "./modules/notifications";
import confirmation from "./modules/buyConfirmation";
import modalBuyTokens from "./modules/modalBuyTokens";
import idleAlertModal from "./modules/idleAlertModal";
import autoBid from "./modules/autoBid";
import onboarding from "./modules/onboarding";
import welcomeModal from "./modules/welcomeModal";
import modalPaymentMethod from "./modules/modalPaymentMethod";
import paymentMethods from "./modules/paymentMethods";

axios.defaults.withCredentials = true;

Vue.use(Vuex);

const vuexLocalStorage = new VuexPersist({
  key: "auctionsStore",
  storage: window.localStorage,
});

const getDefaultState = () => {
  return {
    cfg: cfg,
    user: null,
    isAuthenticated: false,
    loading: false,
    referralId: null,
    isAdmin: false,
    hasNickname: false,
    hasDefaultAddress: false,
    WonAuctions: [],
    TokenPackages: [
      {
        title: "20 BIDS",
        description: "20 Tokens (50c/Bid)",
        price: 1000,
      },
      {
        title: "55 BIDS",
        description: "55 Tokens (45c/Bid)",
        price: 2500,
      },
      {
        title: "115 BIDS",
        description: "115 Tokens (43c/Bid)",
        price: 5000,
      },
      {
        title: "235 BIDS",
        description: "235 Tokens (42c/Bid)",
        price: 10000,
      },
      {
        title: "360 BIDS",
        description: "360 Tokens (41c/Bid)",
        price: 15000,
      },
    ],
  }
}

// initial state
const state = getDefaultState()

export const getters = {
  referralId: (state) => state.referralId,
  TokenPackages: (state) => state.TokenPackages,
  loading: (state) => state.loading,
  user: (state) => state.user,
  WonAuctions: (state) => state.WonAuctions,
  hasDefaultAddress: (state) => state.hasDefaultAddress,
  cfg: (state) => state.cfg
}

export const mutations = {
  SET_USER(state, user) {
    state.user = user;
    if (user == null) {
      state.isAuthenticated = false;
    } else {
      state.isAuthenticated = true;
      if (user.Nickname != null) {
        state.hasNickname = true;
      } else {
        state.hasNickname = false;
      }
    }
  },
  SET_WON_AUCTIONS(state, auctions) {
    state.WonAuctions = auctions;
  },
  SET_REFERRAL_ID(state, payload) {
    state.referralId = payload;
  },
  SET_DEFAULT_ADDRESS: (state, payload) => {
    state.hasDefaultAddress = payload;
  },
  SET_LOADING: (state, value) => {
    state.loading = value;
  },
  SET_TOKEN_PACKAGES: (state, payload) => {
    state.TokenPackages = payload;
  },
  RESET_STORE_STATE(state) {
    Object.assign(state, getDefaultState())
  }
}

export const actions = {
  InitStore: async ({ commit, dispatch }) => {
    // Start store initialization
    await commit("SET_LOADING", true);

    // check and init session
    await dispatch("InitSession");
    await commit("SET_LOADING", false);

    // initialize token packages
    // I don't like this very much!
    const tokenPackages = [
      {
        title: "200 tokens",
        description: "200 Tokens (15c/token)",
        price: 3000,
      },
      {
        title: "400 tokens",
        description: "400 Tokens (15c/token)",
        price: 6000,
      },
      {
        title: "800 tokens",
        description: "800 Tokens (15c/token)",
        price: 12000,
      },
      {
        title: "1600 tokens",
        description: "1600 Tokens (15c/token)",
        price: 24000,
      },
      {
        title: "3200 tokens",
        description: "3200 Tokens (15c/token)",
        price: 48000,
      }
    ];
    await commit("SET_TOKEN_PACKAGES", tokenPackages);
    // Done store initialization
  },
  InitSession: async ({ commit, dispatch }) => {
    // process query params
    const urlParams = new URLSearchParams(window.location.search);
    const referral = urlParams.get("referral_inviter");

    try {
      await axios.get(cfg.ACCOUNTS_API_URL + "sessions/validate");
      await axios.get(
        cfg.API_URL + "sessions/set?referral_inviter=" + referral
      );
      await dispatch("FetchUser");
      await dispatch("CheckDefaultPaymentMethod");
      await dispatch("GetPaymentMethods");
      await dispatch("CheckDefaultAddress");
    } catch (error) {
      commit("SET_USER", null);
    }
  },
  FetchUser: async ({commit}) => {
    await axios
          .get(cfg.API_URL + "users", { withCredentials: true })
          .then((res) => {
            commit("SET_USER", res.data);
            commit("SET_SHOW_ONBOARDING", res.data.ShowWelcome)
          })
          .catch((err) => {
            console.error(err);
            commit("SET_USER", null);
          });
  },
  CheckDefaultPaymentMethod: (state) => {
    axios
      .get(cfg.ACCOUNTS_API_URL + "s/customers/check-default-payment-method")
      .then((response) => {
        if (response.data == "ok") {
          state.commit("SET_DEFAULT_PAYMENT_METHOD", true);
        } else if (response.data == null)
          state.commit("SET_DEFAULT_PAYMENT_METHOD", false);
      })
      .catch((err) => {
        console.error(err);
        state.commit("SET_DEFAULT_PAYMENT_METHOD", false);
      });
  },
  CheckDefaultAddress: (state) => {
    axios.get(`${cfg.ACCOUNTS_API_URL}users/has_address`).then((response) => {
      if (response.data === "ok") {
        state.commit("SET_DEFAULT_ADDRESS", true);
      } else state.commit("SET_DEFAULT_ADDRESS", false);
    });
  },
  setDefaultPaymentMethod: (state, payload) => {
    if (payload == "ok") {
      state.commit("SET_DEFAULT_PAYMENT_METHOD", true);
    } else if (payload == null)
      state.commit("SET_DEFAULT_PAYMENT_METHOD", false);
  },
  FetchWonAuctions: (state) => {
    axios
      .get(cfg.API_URL + "auctions/won", { withCredentials: true })
      .then((res) => {
        state.commit("SET_WON_AUCTIONS", res.data);
      })
      .catch((err) => {
        console.error(err);
        state.commit("SET_WON_AUCTIONS", null);
      });
  },
  SetReferral: (state, payload) => {
    state.commit("SET_REFERRAL_ID", payload);
  },
  SetLoading: (state, value) => {
    state.commit("SET_LOADING", value);
  },
  ResetStore (context) {
    context.commit('RESET_PAYMENT_METHODS_STATE')
    context.commit('RESET_STORE_STATE')
  }
}

export default new Vuex.Store({
  strict: true,
  state,
  mutations,
  actions,
  getters,
  modules: {
    notifications,
    confirmation,
    modalBuyTokens,
    idle: idleAlertModal,
    autoBid,
    onboarding,
    welcomeModal,
    modalPaymentMethod,
    paymentMethods,
  },
  plugins: [vuexLocalStorage.plugin],
});
